import { err, ok } from 'neverthrow';
import type {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { toast } from '@cotera/client/app/components/ui';
import { WorkflowEditorToolbarActionBase } from './workflow-editor-toolbar-action.base';
import {
  targetedDatasetTriggerNodeModel,
  TEST_DATASET_TRIGGER_ACTIVE_TITLE,
} from './workflow-toolbar-action.helpers';

export type OpenWorkflowDatasetView = (params: {
  datasetId: string;
  datasetName: string;
}) => void;

/**
 * Opens the dataset a dataset trigger watches, where the workflow can be run
 * against rows.
 *
 * Unlike cron's and webhook's equivalents this cannot fire on its own: a
 * dataset trigger acts on *a row*, so there is nothing to run until rows are
 * chosen. That used to be a modal with its own copy of the grid and its own row
 * picker; the dataset page already has both, plus the per-row and filtered Run
 * controls and the run status columns showing what came of it — so this drills
 * into the dataset instead and lets the user test from the page that reports
 * the results.
 */
export class WorkflowToolbarTestDatasetAction extends WorkflowEditorToolbarActionBase<
  { t: string; message?: string }[]
> {
  title = 'Test trigger';
  override description = TEST_DATASET_TRIGGER_ACTIVE_TITLE;
  group = 'Workflow';
  icon = 'play';

  constructor(private readonly openDatasetView: OpenWorkflowDatasetView) {
    super();
  }

  override applicable(context: ApplicableContext): boolean {
    return targetedDatasetTriggerNodeModel(context) !== null;
  }

  override async execute(
    _params: Record<string, never>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const dataset = targetedDatasetTriggerNodeModel(context);
    if (dataset === null) {
      return ok({});
    }

    const { datasetId, name } = dataset.snapshotWatchable.snapshot();
    // An unsaved trigger the wizard has not finished binding has no dataset to
    // open yet. Saying so beats a blank panel.
    if (datasetId === '') {
      toast.error('This trigger has no dataset yet');
      return err([{ t: 'error', message: 'Dataset trigger has no dataset' }]);
    }

    this.openDatasetView({ datasetId, datasetName: name });
    return ok({});
  }
}
